/**
 * tv-pdp.js — product page behaviours for the tv-pdp layout.
 *  1. Smart sticky buy rail (desktop): the rail is taller than the viewport, so it anchors its bottom
 *     edge while scrolling down and its top edge while scrolling up, freezing in place in between.
 *  2. Mobile buy bar: shown whenever the main add-to-cart button is outside the viewport (above or below);
 *     mirrors the main button's pending (.loading) state.
 *  3. Variant sync: copies server-rendered rail fragments from the theme's variant-change payload.
 *  4. Summary first: rail links open fold rows (#tv-pdp-fold-*), "+N" badges, recommendation group switcher,
 *     reviews clamped to the first few behind "Vedi tutte", gusto clamped on phones behind «Leggi tutto».
 * Loaded with defer after pubsub.js. Idempotent per host; re-run on shopify:section:load.
 */
(function () {
  var DESKTOP = '(min-width: 990px)';

  function initRail(rail, gallery) {
    if (!rail || !gallery || rail.dataset.tvRailInit) return;
    rail.dataset.tvRailInit = 'true';
    var mq = window.matchMedia(DESKTOP);
    var topMin = parseInt(getComputedStyle(rail).getPropertyValue('--tv-pdp-rail-top'), 10);
    if (isNaN(topMin)) topMin = 24;
    var lastY = window.scrollY, lastH = 0, lastTop = null, mode = '', ticking = false;

    // The buy rail is the layout anchor: it only moves with the page scroll, never because something else
    // changed size. Three states:
    //   'top'    sticky to the top anchor (below Vogal's header when it is showing)
    //   'bottom' sticky to the viewport bottom (rail taller than the viewport, scrolling down)
    //   'held'   kept at its current place (relative offset). When it then ends below the left column, the
    //            column is lengthened (margin-bottom on its last section) so the full-width sections underneath
    //            are pushed down instead of the rail being moved. Lengthening the column rather than giving the
    //            rail a margin keeps the grid from spreading the extra height between the left sections.
    var anchorTop = null;   // 'top' mode: null = the regular top anchor (follows the header), else a screen position
    function stick(px, m) {
      if (mode === m && rail.style.top === px + 'px') return;
      // Animate only between two sticky anchors (the header sliding in or out)
      rail.style.transition = mode === 'top' || mode === 'bottom' ? 'top 0.3s ease' : '';
      mode = m;
      rail.style.position = 'sticky';
      rail.style.top = px + 'px';
    }
    function hold() {
      var offset = Math.max(0, rail.getBoundingClientRect().top - gallery.getBoundingClientRect().top);
      mode = 'held';
      rail.style.transition = '';
      rail.style.position = 'relative';
      rail.style.top = offset + 'px';
    }
    // Left-column sections beside the rail: those before the first full-width section that follows the rail
    // (the recommendation rows, sections/tv-pdp-rows). The rail never moves past that section.
    function besideLefts() {
      var host = rail.closest('.main-content');
      if (!host) return [];
      var stop = host.querySelector(':scope > .tv-pdp-rows-host');
      return Array.prototype.filter.call(host.querySelectorAll(':scope > .tv-pdp-left:not([data-tv-pdp-area-source])'), function (s) {
        return !stop || (s.compareDocumentPosition(stop) & Node.DOCUMENT_POSITION_FOLLOWING);
      });
    }
    var pad = 0, padEl = null;
    function setPad(px) {
      px = Math.max(0, Math.round(px));
      var lefts = besideLefts();
      var el = lefts.length ? lefts[lefts.length - 1] : gallery;
      if (padEl && padEl !== el) padEl.style.marginBottom = '';
      padEl = el;
      if (px === pad && el.style.marginBottom === (px ? px + 'px' : '')) return;
      pad = px;
      el.style.marginBottom = px ? px + 'px' : '';
    }
    var watchedHdr = null, hdrObserver = null;
    function headerOffset() {
      // Vogal's header re-appears as a sticky bar (.sticky_hdr) while scrolling up; keep the rail below it.
      // theme.js builds .hdr_wrap after this script runs, so find it lazily and re-apply when its class flips.
      var hdr = document.querySelector('.hdr_wrap');
      if (hdr && hdr !== watchedHdr && 'MutationObserver' in window) {
        if (hdrObserver) hdrObserver.disconnect();
        hdrObserver = new MutationObserver(tick);
        hdrObserver.observe(hdr, { attributes: true, attributeFilter: ['class'] });
        watchedHdr = hdr;
      }
      return hdr && hdr.classList.contains('sticky_hdr') ? hdr.offsetHeight : 0;
    }
    function columnBottom() {
      // Bottom of the left column (gallery + tv-pdp-left sections)
      var bottom = gallery.getBoundingClientRect().bottom;
      besideLefts().forEach(function (s) {
        var b = s.getBoundingClientRect().bottom;
        if (b > bottom) bottom = b;
      });
      return bottom;
    }
    function apply() {
      ticking = false;
      if (!mq.matches) {
        if (mode !== '') { rail.style.position = ''; rail.style.top = ''; rail.style.transition = ''; setPad(0); mode = ''; }
        return;
      }
      var vh = window.innerHeight, h = rail.offsetHeight, y = window.scrollY;
      var moved = y !== lastY, down = y > lastY;
      lastY = y;
      var grew = lastH && h !== lastH;
      lastH = h;
      var r = rail.getBoundingClientRect(), g = gallery.getBoundingClientRect();
      var top = topMin + headerOffset();
      var tall = h + top + topMin > vh;
      var bottomTop = vh - h - topMin;
      // The column may have been lengthened for a held rail (pad); sticky must stay inside it, because sticky is
      // bounded by the whole grid container and would otherwise slide over the full-width sections below
      var extBottom = columnBottom() + pad;
      function fits(px) { return Math.max(g.top, px) + h <= extBottom + 1; }

      if (mode === '') {
        anchorTop = null;
        if (fits(top)) stick(top, 'top'); else hold();
      } else if (mode === 'top' || mode === 'bottom') {
        // Scrolled back up to where the rail's own place in the column is on screen: glide home
        if (mode === 'top' && anchorTop !== null && moved && !down && g.top >= top - 1) anchorTop = null;
        var want = mode === 'top' ? (anchorTop === null ? top : Math.max(anchorTop, top)) : bottomTop;
        if ((grew && mode === 'bottom') || !fits(want) ||
            (moved && tall && (mode === 'top' ? down : !down)) ||
            (moved && down && mode === 'top' && anchorTop !== null && anchorTop > top + 1)) {
          // Something resized (accordion, tab switch), the column ends, or the scroll direction needs the
          // other edge: stay exactly where we are
          hold();
        } else if (mode === 'top') {
          stick(want, 'top');   // follows the header sliding in / out
        }
      } else if (moved) {
        // Held: rejoin a sticky anchor only where that does not move the rail
        if (!down && lastTop !== null && lastTop >= top && r.top >= top) {
          // Already below the top anchor (held there by a layout change) while scrolling up: stay at this spot on
          // screen until the rail is back in its place in the column (sticky at the current position)
          if (fits(r.top)) { anchorTop = Math.round(r.top); stick(anchorTop, 'top'); }
        } else {
          var target = !tall || !down ? top : bottomTop;
          var crossed = lastTop !== null && (lastTop - target) * (r.top - target) <= 0;
          if (crossed && fits(target)) {
            if (!tall || !down) { anchorTop = null; stick(top, 'top'); } else stick(bottomTop, 'bottom');
          }
        }
      }
      // Lengthen the column under a held rail; while sticky the extra length may only shrink, so scrolling never
      // grows the page
      var need = rail.getBoundingClientRect().bottom - columnBottom();
      setPad(mode === 'held' ? need : Math.min(pad, need));
      lastTop = rail.getBoundingClientRect().top;
    }
    function tick() { if (!ticking) { ticking = true; window.requestAnimationFrame(apply); } }
    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick);
    if ('ResizeObserver' in window) {
      // Watch the left column too: tab switches and lazy content change it without any scroll
      var ro = new ResizeObserver(tick);
      ro.observe(rail);
      ro.observe(gallery);
      var main = rail.closest('.main-content');
      if (main) {
        ro.observe(main);
        main.querySelectorAll(':scope > .tv-pdp-left').forEach(function (s) { ro.observe(s); });
      }
    }
    apply();
  }

  function initBar(bar, atc) {
    if (!bar || !atc || bar.dataset.tvBarInit || !('IntersectionObserver' in window)) return;
    bar.dataset.tvBarInit = 'true';
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      // Shown only once the main button has been scrolled past (it left the screen through the top). The root reaches
      // far below the screen, so a button not reached yet (below the first screen) still counts as in view: the bar
      // never shows on the first screen, and a jump straight back to the top hides it again.
      var show = !e.isIntersecting;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
    }, { threshold: 0, rootMargin: '0px 0px 100000px 0px' });
    io.observe(atc);

    // cart.js puts the pending state (.loading + spinner) only on the form's own submit button;
    // mirror it onto the bar button, which submits the same form from outside it.
    function syncPending() {
      var btn = bar.querySelector('.tv-pdp-bar__btn');
      if (!btn) return;
      var pending = atc.classList.contains('loading');
      btn.classList.toggle('loading', pending);
      btn.setAttribute('aria-busy', String(pending));
      var spinner = btn.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !pending);
    }
    if ('MutationObserver' in window) {
      new MutationObserver(syncPending).observe(atc, { attributes: true, attributeFilter: ['class'] });
    }
  }

  function initSync(sid) {
    if (typeof window.subscribe !== 'function') return;
    if (window.__tvPdpSync && window.__tvPdpSync[sid]) return;
    window.__tvPdpSync = window.__tvPdpSync || {};
    window.__tvPdpSync[sid] = true;
    // vendor.js declares PUB_SUB_EVENTS as a top-level const and mirrors it onto window.
    /* global PUB_SUB_EVENTS */
    var events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : window.PUB_SUB_EVENTS;
    var eventName = (events && events.variantChange) || 'variant-change';
    window.subscribe(eventName, function (e) {
      if (!e || !e.data || !e.data.html || String(e.data.sectionId) !== String(sid)) return;
      ['TvDims-', 'TvExpiry-', 'TvTitleChip-', 'TvEyebrow-', 'TvPdpBar-', 'TvRestock-'].forEach(function (prefix) {
        var src = e.data.html.getElementById(prefix + sid);
        var dst = document.getElementById(prefix + sid);
        if (!src || !dst) return;
        dst.innerHTML = src.innerHTML;
        if (src.hasAttribute('hidden')) dst.setAttribute('hidden', ''); else dst.removeAttribute('hidden');
      });
    });
  }

  function initHost(host) {
    if (host.dataset.tvPdpInit) return;
    host.dataset.tvPdpInit = 'true';
    var sid = host.dataset.sectionId;
    initRail(document.getElementById('TvPdpRail-' + sid), host.querySelector('.tv-pdp__gallery'));
    initBar(document.getElementById('TvPdpBar-' + sid), document.getElementById('ProductSubmitButton-' + sid));
    initSync(sid);
  }

  // Judge.me summary: the board shows the average as a large number next to the stars.
  function initReviews(box) {
    // Judge.me prints "Write a review" hidden and only reveals and translates it once the widget scrolls into
    // view, so it popped in after the card. Show it right away with the same localized label.
    var writeLink = box.querySelector('.jdgm-write-rev-link');
    if (writeLink && box.dataset.writeLabel) {
      writeLink.textContent = box.dataset.writeLabel;
      writeLink.style.removeProperty('display');
    }
    function addAverage() {
      var widget = box.querySelector('.jdgm-rev-widg[data-average-rating]');
      var summary = widget && widget.querySelector('.jdgm-rev-widg__summary');
      if (!summary || summary.querySelector('.tv-jdgm-avg')) return;
      var avg = parseFloat(widget.getAttribute('data-average-rating'));
      if (!(avg > 0)) return;
      var span = document.createElement('span');
      span.className = 'tv-jdgm-avg';
      span.textContent = avg.toLocaleString(document.documentElement.lang || 'it', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      summary.insertBefore(span, summary.firstChild);
    }
    addAverage();
    // Review dates come in the server HTML (datetime) but stay a spinner until Judge.me's script formats them, in US
    // order. Write them ourselves in the page's language (14/09/2026), whether or not Judge.me runs; Judge.me
    // rewriting a date is put back by the observer below.
    var dateFormat = window.Intl && Intl.DateTimeFormat
      ? new Intl.DateTimeFormat(document.documentElement.lang || 'it', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null;
    function fillDates() {
      if (!dateFormat) return;
      var times = box.querySelectorAll('time.jdgm-rev__timestamp[datetime]');
      for (var i = 0; i < times.length; i++) {
        var at = new Date(times[i].getAttribute('datetime'));
        if (isNaN(at.getTime())) continue;
        var text = dateFormat.format(at);
        if (times[i].textContent !== text) times[i].textContent = text;
        times[i].classList.remove('jdgm-spinner');
      }
    }
    fillDates();
    // Show the first `data-visible` reviews; the rest and the pager wait behind "Vedi tutte"
    var visible = parseInt(box.dataset.visible, 10);
    var moreBtn = box.parentElement && box.parentElement.querySelector('[data-tv-reviews-more]');
    function clamp() {
      if (!moreBtn || !(visible > 0) || box.dataset.tvExpanded) return;
      var revs = box.querySelectorAll('.jdgm-rev-widg__reviews > .jdgm-rev');
      var pager = box.querySelector('.jdgm-paginate');
      var widget = box.querySelector('.jdgm-rev-widg');
      var total = widget ? parseInt(widget.getAttribute('data-number-of-reviews'), 10) : 0;
      var extra = revs.length > visible || total > revs.length || (pager && pager.children.length > 1);
      for (var i = 0; i < revs.length; i++) {
        if (i >= visible) revs[i].setAttribute('data-tv-clamped', ''); else revs[i].removeAttribute('data-tv-clamped');
      }
      box.classList.toggle('is-clamped', !!extra);
      moreBtn.hidden = !extra;
      // The button sits under the review list (right column on desktop), right-aligned; Judge.me renders late
      var list = box.querySelector('.jdgm-rev-widg__reviews');
      if (list && list.nextElementSibling !== moreBtn) list.insertAdjacentElement('afterend', moreBtn);
    }
    if (moreBtn && !moreBtn.dataset.tvBound) {
      moreBtn.dataset.tvBound = 'true';
      moreBtn.addEventListener('click', function () {
        box.dataset.tvExpanded = 'true';
        box.classList.remove('is-clamped');
        box.querySelectorAll('[data-tv-clamped]').forEach(function (r) { r.removeAttribute('data-tv-clamped'); });
        moreBtn.hidden = true;
        loadRest();
      });
    }
    // "Vedi tutte" shows every review: the first page is in the server HTML, the others are read by us from Judge.me's
    // public widget feed (no token, CORS open), whether or not Judge.me's own script runs. Judge.me stays the store of
    // the reviews. Its pager is hidden once everything is in; if a read fails the pager stays as the fallback.
    function loadRest() {
      var widget = box.querySelector('.jdgm-rev-widg');
      var list = box.querySelector('.jdgm-rev-widg__reviews');
      var host = box.querySelector('#judgeme_product_reviews, .jdgm-review-widget[data-id]');
      var shop = window.Shopify && window.Shopify.shop;
      var total = widget ? parseInt(widget.getAttribute('data-number-of-reviews'), 10) : 0;
      var pager = box.querySelector('.jdgm-paginate[data-per-page]');
      var perPage = pager ? parseInt(pager.getAttribute('data-per-page'), 10) : 5;
      if (!list || !host || !shop || !window.fetch || box.dataset.tvAllLoading) return;
      var have = list.querySelectorAll(':scope > .jdgm-rev').length;
      if (!(total > have) || !(perPage > 0)) return;
      box.dataset.tvAllLoading = 'true';
      var updated = Date.parse(widget.getAttribute('data-updated-at') || '');
      var base = 'https://cdn.judge.me/reviews/reviews_for_widget?url=' + encodeURIComponent(shop) + '&shop_domain=' + encodeURIComponent(shop)
        + '&platform=shopify&per_page=' + perPage + '&product_id=' + encodeURIComponent(host.getAttribute('data-id'))
        + '&sort_by=created_at&sort_dir=desc' + (updated ? '&ts=' + Math.floor(updated / 1000) : '');
      var pages = [];
      for (var page = Math.floor(have / perPage) + 1; page <= Math.ceil(total / perPage); page++) pages.push(page);
      var seen = {};
      list.querySelectorAll(':scope > .jdgm-rev[data-review-id]').forEach(function (r) { seen[r.getAttribute('data-review-id')] = true; });
      pages.reduce(function (chain, n) {
        return chain.then(function () {
          return fetch(base + '&page=' + n, { credentials: 'omit' }).then(function (res) {
            if (!res.ok) throw new Error('reviews_page_' + res.status);
            return res.json();
          }).then(function (data) {
            var tpl = document.createElement('template');
            tpl.innerHTML = (data && data.html) || '';
            tpl.content.querySelectorAll('.jdgm-rev').forEach(function (r) {
              var id = r.getAttribute('data-review-id');
              if (id && seen[id]) return;
              if (id) seen[id] = true;
              list.appendChild(document.importNode(r, true));
            });
          });
        });
      }, Promise.resolve()).then(function () {
        box.classList.add('is-all-loaded');
      }, function () {
        delete box.dataset.tvAllLoading;
      });
    }
    clamp();
    if (box.dataset.tvReviewsInit || !window.MutationObserver) return;
    box.dataset.tvReviewsInit = 'true';
    // Paging or re-sorting swaps the review list (spinner first, then fewer cards on the last page):
    // keep the list at least as tall as it was so the page below does not jump.
    function lockHeight() {
      // Lock the widget, not the list: Judge.me hides the whole list behind a spinner while loading
      var widget = box.querySelector('.jdgm-rev-widg');
      if (widget && widget.offsetHeight) widget.style.minHeight = Math.max(widget.offsetHeight, parseFloat(widget.style.minHeight) || 0) + 'px';
    }
    // After paging Judge.me jumps the window to the widget (jdgm.scrollTo -> jdgmSettings.scrollTo hook).
    // Take that over: stay put when the target is already on screen, otherwise glide up just enough to show it
    // below Vogal's header (which slides in on scroll-up).
    function takeOverScroll() {
      var s = window.jdgmSettings;
      if (!s || s.tvScrollHook) return;
      s.tvScrollHook = true;
      var pending = null;
      s.scrollTo = function (target) {
        var el = target && target[0] ? target[0] : target;
        if (!el || !el.getBoundingClientRect || pending) return false;
        // Judge.me calls this before the new page is in: wait for the swap (spinner hidden, list back),
        // otherwise the glide fights the content change and the browser's scroll anchoring
        var tries = 0;
        pending = setInterval(function () {
          var spinner = box.querySelector('.jdgm-rev-widg__paginate-spinner-wrapper');
          var loading = spinner && spinner.offsetParent !== null;
          if (loading && ++tries < 40) return;
          clearInterval(pending);
          pending = null;
          var top = el.getBoundingClientRect().top;
          var hdr = document.querySelector('.hdr_wrap');
          var cover = hdr ? hdr.offsetHeight : 0;
          if (top >= cover && top < window.innerHeight - 80) return;
          window.scrollTo({ top: window.scrollY + top - cover - 16, behavior: 'smooth' });
        }, 50);
        return false;
      };
    }
    takeOverScroll();
    function beforeChange() { takeOverScroll(); lockHeight(); }
    box.addEventListener('click', function (e) { if (e.target.closest('.jdgm-paginate__page')) beforeChange(); }, true);
    box.addEventListener('keydown', function (e) { if (e.key === 'Enter' && e.target.closest('.jdgm-paginate__page')) beforeChange(); }, true);
    box.addEventListener('mousedown', function (e) { if (e.target.closest('.jdgm-sort-dropdown')) beforeChange(); }, true);
    new MutationObserver(function () { addAverage(); fillDates(); clamp(); }).observe(box, { childList: true, subtree: true });
  }

  // Rail links to fold rows: open the row (or the label accordion's panel) and bring it below the header.
  function foldTarget(link) {
    var keys = (link.getAttribute('data-tv-fold-link') || '').split(',');
    var href = link.getAttribute('href') || '';
    var el = href.charAt(0) === '#' ? document.getElementById(href.slice(1)) : null;
    for (var i = 0; !el && i < keys.length; i++) el = document.getElementById('tv-pdp-fold-' + keys[i].trim());
    return el;
  }
  function openFold(el) {
    if (el.tagName === 'DETAILS') el.open = true;
    var toggle = el.querySelector('.pinfo3__toggle[aria-expanded="false"]');
    if (toggle) toggle.click();
    var hdr = document.querySelector('.hdr_wrap');
    var cover = hdr ? hdr.offsetHeight : 0;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: window.scrollY + el.getBoundingClientRect().top - cover - 16, behavior: reduce ? 'auto' : 'smooth' });
  }
  function initFolds() {
    document.querySelectorAll('[data-tv-fold-link]').forEach(function (link) {
      // No row to open (e.g. the product has no prep plan): keep the tile as plain information
      if (!foldTarget(link)) link.classList.add('is-static');
    });
    if (document.documentElement.dataset.tvFoldsInit) return;
    document.documentElement.dataset.tvFoldsInit = 'true';
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-tv-fold-link]');
      if (!link) return;
      var el = foldTarget(link);
      if (!el) return;
      e.preventDefault();
      openFold(el);
    });
    var hash = window.location.hash.slice(1);
    var target = hash.indexOf('tv-pdp-fold-') === 0 && document.getElementById(hash);
    if (target && target.tagName === 'DETAILS') target.open = true;
  }

  function initBadges(box) {
    var btn = box.querySelector('[data-tv-badges-more]');
    if (!btn || btn.dataset.tvBound) return;
    btn.dataset.tvBound = 'true';
    btn.addEventListener('click', function () {
      box.querySelectorAll('.tv-pdp-badges__extra[hidden]').forEach(function (b) { b.hidden = false; });
      btn.remove();
    });
  }

  // Recommendation groups share one card row; the tab list switches the group (roving tabindex, arrow keys).
  function initRecs(recs) {
    var list = recs.querySelector('.tv-pdp-recs__tabs');
    if (!list || recs.dataset.tvRecsInit) return;
    recs.dataset.tvRecsInit = 'true';
    var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        // Scoped to the card: the extension panel moves it into the interaction host's Shadow DOM
        var panel = recs.querySelector('[id="' + t.getAttribute('aria-controls') + '"]');
        if (panel) panel.classList.toggle('is-active', on);
      });
      if (focus) tab.focus();
    }
    list.addEventListener('click', function (e) {
      var tab = e.target.closest('[role="tab"]');
      if (tab) select(tab);
    });
    list.addEventListener('keydown', function (e) {
      var i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); select(tabs[(i + 1) % tabs.length], true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); select(tabs[(i - 1 + tabs.length) % tabs.length], true); }
    });
    list.hidden = false;
    recs.classList.add('is-tabbed');
    select(tabs[0]);
  }

  // Gusto in the rail (block tv_points): phones show the first lines (tv-pdp.css clamps the list) and «Leggi tutto»
  // opens the rest. The button only shows while the clamp actually hides text; desktop does not clamp.
  function initPointsMore(wrap) {
    var btn = wrap.querySelector('[data-tv-points-more]');
    var list = wrap.querySelector('.tv-pdp-points');
    if (!btn || !list || wrap.dataset.tvPointsInit) return;
    wrap.dataset.tvPointsInit = 'true';
    var more = btn.textContent, less = btn.dataset.lessLabel || more;
    function update() {
      if (wrap.classList.contains('is-open')) return;
      btn.hidden = list.scrollHeight <= list.clientHeight + 1;
    }
    btn.addEventListener('click', function () {
      var open = wrap.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', String(open));
      btn.textContent = open ? less : more;
      if (!open) update();
    });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(list); else window.addEventListener('resize', update);
    update();
  }

  function boot() {
    document.querySelectorAll('[data-tv-pdp-host]').forEach(initHost);
    document.querySelectorAll('.tv-pdp-reviews').forEach(initReviews);
    document.querySelectorAll('.tv-pdp-badges').forEach(initBadges);
    document.querySelectorAll('.tv-pdp-recs').forEach(initRecs);
    document.querySelectorAll('.tv-pdp-points-wrap').forEach(initPointsMore);
    initFolds();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
